import type { QueryClient } from "@tanstack/react-query"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router"
import type { ReactNode } from "react"
import * as z from "zod"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { sharedConfig } from "~/lib/config/shared"
import { getCurrentUserQueryOptions } from "~/modules/auth/auth.queries"
import type { SsrSafeUser } from "~/modules/auth/auth.types"
import { DefaultCatchBoundary } from "~/root-components/default-catch-boundary"
import { NotFound } from "~/root-components/not-found"
import { AppProviders } from "~/root-components/providers/app-providers"
import styles from "~/styles/app.css?url"

const appRoleSchema = z.object({
  id: z.coerce.number<number>(),
  name: z.string(),
})

type AppRole = z.infer<typeof appRoleSchema>

interface RouterContext {
  queryClient: QueryClient
  user: SsrSafeUser | null
  appRole: AppRole | null
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      getCurrentUserQueryOptions(),
    )

    let appRole: AppRole | null = null

    if (user) {
      const appRoleResult = appRoleSchema.safeParse(user?.app_metadata.app_role)

      if (appRoleResult.success) {
        appRole = appRoleResult.data
      }
    }

    return {
      appRole,
      user,
      pageTitle: sharedConfig.appName,
    }
  },
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    )
  },
  notFoundComponent: (props) => (
    <RootDocument>
      <NotFound {...props} />
    </RootDocument>
  ),
  component: RootComponent,
  head: ({ match }) => ({
    meta: generateMetaTags({
      base: {
        title: match.context.pageTitle,
        description: `Central Management System for ${sharedConfig.appName}`,
        url: sharedConfig.baseUrl,
      },
    }),
    links: [
      // 1. DNS prefetch for early connection setup
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://fonts.gstatic.com" },

      // 2. Preconnect for early connection setup
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },

      // 3. Preload critical resources
      { rel: "preload", href: styles, as: "style" },

      // 4. Stylesheets (render-blocking resources)
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      },
      { rel: "stylesheet", href: styles },

      // 5. Icons and manifest (less critical resources)
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#000099" },
      { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
    ],
  }),
})

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning className="flex min-h-screen flex-col">
        <AppProviders>{children}</AppProviders>
        <Scripts />
      </body>
    </html>
  )
}
